import React, { useState } from 'react';

import { Autocomplete, TextField, Typography, useTheme } from '@mui/material';
import type { GridFilterInputValueProps } from '@mui/x-data-grid-premium';
import clsx from 'clsx';
import { keys } from 'ramda';
import { AlineCheckbox, FilterTags } from 'src/components';
import { Apollo } from 'src/gql/generated';
import { makeStyles } from 'tss-react/mui';

import { filterAutocompleteStyle } from './autocomplete-filter-utils';
import { ClickableColumnIcon } from '../../workflow/board/settings/ColumnItem';

const useStyles = makeStyles()(() => ({
  autocomplete: filterAutocompleteStyle,
  item: {
    alignItems: 'center',
    display: 'flex',
    gap: 8,
  },
}));

const CATEGORY_RECORD: Record<Apollo.ColumnCategory, string> = {
  [Apollo.ColumnCategory.Triage]: 'Triage',
  [Apollo.ColumnCategory.NotStarted]: 'Not Started',
  [Apollo.ColumnCategory.Started]: 'Started',
  [Apollo.ColumnCategory.Done]: 'Done',
  [Apollo.ColumnCategory.Paused]: 'Paused',
  [Apollo.ColumnCategory.Cancelled]: 'Cancelled',
};
const CATEGORY_OPTIONS: Apollo.ColumnCategory[] = keys(CATEGORY_RECORD);

function getCategoryLabel(category: Apollo.ColumnCategory): string {
  return CATEGORY_RECORD[category];
}

export function ColumnCategoryAutocompleteFilter({
  applyValue,
  item,
}: GridFilterInputValueProps): JSX.Element {
  const theme = useTheme();
  const { classes } = useStyles();
  const [truncateTags, setTruncateTags] = useState(true);
  const [value, setValue] = useState<Apollo.ColumnCategory[]>(item.value ?? []);

  function handleValueChange(newValue: Apollo.ColumnCategory[]): void {
    applyValue({ ...item, value: newValue });
    setValue(newValue);
  }

  return (
    <Autocomplete<Apollo.ColumnCategory, true, false, false>
      disableCloseOnSelect
      fullWidth
      multiple
      openOnFocus
      blurOnSelect={false}
      className={classes.autocomplete}
      getOptionLabel={getCategoryLabel}
      options={CATEGORY_OPTIONS}
      renderInput={params => (
        <TextField placeholder="Search status types..." {...params} variant="standard" />
      )}
      renderOption={(props, option, state) => {
        const { className, ...otherProps } = props;
        return (
          <li {...otherProps} className={clsx(className, classes.item)}>
            <AlineCheckbox checked={state.selected} style={{ marginRight: theme.spacing(1) }} />
            <ClickableColumnIcon
              category={option}
              color={null}
              iconSize={16}
              showBackground={false}
              showBorder={false}
            />
            <Typography>{getCategoryLabel(option)}</Typography>
          </li>
        );
      }}
      renderValue={tags => (
        <FilterTags<Apollo.ColumnCategory>
          getTextFromTag={getCategoryLabel}
          tags={tags}
          truncateTags={truncateTags}
          onDelete={tag => handleValueChange(tags.filter(t => t !== tag))}
        />
      )}
      slotProps={{
        popper: {
          modifiers: [
            { enabled: false, name: 'flip' },
            { enabled: false, name: 'preventOverflow' },
          ],
        },
      }}
      value={value}
      onBlur={e => {
        e.stopPropagation();
        setTruncateTags(true);
      }}
      onChange={(_e, newValue) => handleValueChange(newValue)}
      onFocus={e => {
        e.stopPropagation();
        setTruncateTags(false);
      }}
    />
  );
}
